import {
  createCodeVerifier,
  createCodeChallenge,
  generateState,
} from "@/utils/pkce";
import config from "@/config";

export interface OAuth2Tokens {
  access_token: string
  refresh_token: string
  token_type?: string
  expires_in?: number
  scope?: string
}

export class OAuth2Service {
  private clientId: string;
  private redirectUri: string;
  private authServerBase: string;
  private tokenEndpoint: string;

  constructor(clientId: string, redirectUri: string, authServerBase: string) {
    this.clientId = clientId;
    this.redirectUri = redirectUri;
    this.authServerBase = authServerBase;
    this.tokenEndpoint = `${authServerBase}/api/v1/oauth2/token`;
  }

  async startAuthorization(scopes: string = "profile email vendor"): Promise<void> {
    const codeVerifier = createCodeVerifier();
    const codeChallenge = await createCodeChallenge(codeVerifier);
    const state = generateState();

    sessionStorage.setItem("oauth_code_verifier", codeVerifier);
    sessionStorage.setItem("oauth_state", state);

    const authUrl = new URL(`${this.authServerBase}/oauth/authorize`);
    authUrl.searchParams.set("client_id", this.clientId);
    authUrl.searchParams.set("redirect_uri", this.redirectUri);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("scope", scopes);
    authUrl.searchParams.set("state", state);
    authUrl.searchParams.set("code_challenge", codeChallenge);
    authUrl.searchParams.set("code_challenge_method", "S256");

    window.location.href = authUrl.toString();
  }

  async handleCallback(callbackUrl: string): Promise<OAuth2Tokens> {
    const url = new URL(callbackUrl);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    const error = url.searchParams.get("error");

    if (error) {
      throw new Error(`Authorization failed: ${error}`);
    }

    if (!code) {
      throw new Error("No authorization code received");
    }

    const storedState = sessionStorage.getItem("oauth_state");
    if (state !== storedState) {
      throw new Error("Invalid state parameter");
    }

    const codeVerifier = sessionStorage.getItem("oauth_code_verifier");
    const tokens = await this.exchangeCodeForTokens(code, codeVerifier!);

    sessionStorage.removeItem("oauth_code_verifier");
    sessionStorage.removeItem("oauth_state");

    return tokens;
  }

  async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<OAuth2Tokens> {
    const response = await fetch(this.tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: this.clientId,
        code: code,
        code_verifier: codeVerifier,
        redirect_uri: this.redirectUri,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || "Token exchange failed");
    }

    return response.json();
  }

  async refreshToken(refreshToken: string): Promise<OAuth2Tokens> {
    const response = await fetch(this.tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: this.clientId,
      }),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    return response.json();
  }
}

export default new OAuth2Service(
  config.oauth2.clientId,
  config.oauth2.redirectUri,
  config.oauth2.authServerBase
);
